import { Box, Breadcrumb, Button, Flex } from '@cowsquare/design';
import { Link } from 'gatsby';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React, { useCallback } from 'react';
import { Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import defaultAuthorAvatar from '../../../../asset/defaultAuthorAvatar.png';
import { composeGoogleMapUrl } from '../../../../utils/composeGoogleMapUrl';
import Map from '../../../Map';
import { Field, RowLink, RowMap, RowTitle } from '../../../MedicalContent';
import { Section } from '../../../Section';
import PageNotFound from '../../error/404';

const BreadcrumbWrapper = styled(Box)`
  ${layout}
`;

const Title = styled(Box)`
  font-size: ${themeGet('fontSizes.h3')};
  font-weight: 600;
  line-height: 1.33;
  color: ${themeGet('colors.blacks.0')};
`;

const AvatarTitle = styled(Box)`
  font-size: ${themeGet('fontSizes.h2')};
  line-height: 1.42;
`;

const GET_MEMBER_RECORD = gql`
  query getMemberRecord($id: uuid!) {
    record: hkdutest_doctors_by_pk(id: $id) {
      id
      name
      fax
      address
      email
      wechat
      whatsapp
      phone
      hkdu_member_no
      member_join_no
      register_no
      created_at
      gender {
        name
      }
      languages {
        language {
          id
          name
        }
      }
      clinics {
        id
        name
        phone
        fax
        address
        address_geo
        emergency_service_concat
        out_going_service_supported
        emergency_service_supported
        vaccine_service_supported
        insurance_scheme_supported
        medical_cards_supported
        payment_cards_supported
        opening_time {
          day
          start_time
          end_time
          speical_date
        }
        support_government_program {
          government_program {
            id
            name
          }
        }
      }
      hospitals {
        hospital {
          id
          name
          website
        }
      }
      basic_qualifications {
        basic_qualification {
          id
          name
        }
      }
      recognized_specialties {
        recognized_specialty {
          id
          name
        }
      }
      professional_qualifications {
        professional_qualification {
          id
          name
        }
      }
    }
  }
`;

const AvatarBox = styled(Box).attrs({
  mr: [0, 5],
  mb: [5, 0],
})`
  border-radius: ${themeGet('radii.5')};
`;

const AvatarWrapper = styled(Flex).attrs({
  flexDirection: ['column', 'row'],
  alignItems: ['flex-start', 'center'],
})``;

const InfoLabel = styled(Box).attrs({
  mr: [0, 5],
  mb: [4, 0],
})`
  color: ${themeGet('colors.blacks.5')};
  width: 236px;
  line-height: 1.43;
`;

const InfoBox = styled(Box).attrs({
  py: ({ py }) => py || 5,
  mb: ({ mb }) => mb || 5,
})``;

const LinkCard = styled(Box)`
  height: 80px;
  padding: 10px 20px;
  background-color: ${themeGet('colors.whites.0')};
  border: ${themeGet('borders.1')} ${themeGet('colors.blacks.8')};

  &:hover {
    cursor: pointer;
  }
`;

const LinkButton = styled(Button)`
  padding: 0;
`;

const HyperLink = styled.a`
  text-decoration: none;
  color: ${themeGet('colors.link')};
  display: inline-block;
`;

const showAvailableOrNot = condi => (condi ? 'Available' : 'Not available');

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getAMPM = hour => (hour >= 12 ? 'pm' : 'am');
const get12HourFrom24Hour = hour => {
  const newHour = hour % 12;
  return newHour === 0 ? 12 : newHour;
};

const generateRowValue = path =>
  R.addIndex(R.map)((v, index) => <div key={index}>{R.pathOr('', path, v)}</div>);

const MemberContent = ({ id }) => {
  const handleMarkerClick = useCallback(url => {
    window.open(url);
  }, []);

  return (
    <Query query={GET_MEMBER_RECORD} variables={{ id }}>
      {({ data, loading }) => {
        if (!loading && !R.prop('record', data)) {
          return <PageNotFound />;
        }

        const record = R.propOr({}, 'record', data);

        const title = R.propOr('', 'name', record);
        const clinics = R.propOr([], 'clinics', record);
        const gender = R.pathOr('', ['gender', 'name'], record);

        const languages = R.pipe(R.propOr([], 'languages'), R.chain(R.pathOr({}, ['language'])))(record);

        const hospitals = R.pipe(R.propOr([], 'hospitals'), R.chain(R.pathOr({}, ['hospital'])))(record);

        const basicQualifications = R.pipe(
          R.propOr([], 'basic_qualifications'),
          R.chain(R.pathOr({}, ['basic_qualification']))
        )(record);

        const professionalQualifications = R.pipe(
          R.propOr([], 'professional_qualifications'),
          R.chain(R.pathOr({}, ['professional_qualification']))
        )(record);

        const recognizedSpecialties = R.pipe(
          R.propOr([], 'recognized_specialties'),
          R.chain(R.pathOr({}, ['recognized_specialty']))
        )(record);

        return (
          <Section>
            <Helmet>
              <title>{title} - HKDU Members Only</title>
            </Helmet>
            <Box px={[5, 0]}>
              <BreadcrumbWrapper my={5} display={['none', 'block']}>
                <Breadcrumb>
                  <Breadcrumb.Item linkProps={{ as: Link, to: '/member-only/members' }}>
                    Members
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{title}</Breadcrumb.Item>
                </Breadcrumb>
              </BreadcrumbWrapper>
              <Box py={5}>
                <AvatarWrapper>
                  <AvatarBox>
                    <img src={defaultAuthorAvatar} alt="" />
                  </AvatarBox>
                  <AvatarTitle>{title}</AvatarTitle>
                </AvatarWrapper>
              </Box>
              <InfoBox>
                <RowTitle>Contact Information</RowTitle>
                <Field label="Registration No" value={record.register_no} />
                <Field label="HKDU Membership No" value={record.hkdu_member_no} />
                <Field label="Membership Joining Iteration" value={record.member_join_no} />
              </InfoBox>
              <InfoBox>
                <RowTitle>Member Details</RowTitle>
                <Field label="Gender" value={gender} />
                <Field label="Language" value={languages} render={generateRowValue(['name'])} />
                <Field
                  label="E-mail"
                  value={record.email}
                  render={value => <RowLink href={`mailto:${value}`}>{value}</RowLink>}
                />
                <Field label="Mobile" value={record.phone} />
                <Field label="Fax" value={record.fax} />
                <Field label="Whatsapp" value={record.whatsapp} />
                <Field label="WeChat" value={record.wechat} />
                <Field label="Correspondence Address" value={record.address} />
              </InfoBox>

              <InfoBox>
                <RowTitle>Qualifications</RowTitle>
                <Field
                  label="Basic Qualifications"
                  value={basicQualifications}
                  render={generateRowValue(['name'])}
                />
                <Field
                  label="Professional Qualifications"
                  value={professionalQualifications}
                  render={generateRowValue(['name'])}
                />
                <Field
                  label="Recognized Specialties"
                  value={recognizedSpecialties}
                  render={generateRowValue(['name'])}
                />
              </InfoBox>

              {clinics.map(clinic => (
                <InfoBox key={clinic.id}>
                  <RowTitle>Service Details - {clinic.name}</RowTitle>
                  <Field label="Phone" value={clinic.phone} />
                  <Field label="Fax" value={clinic.fax} />
                  <Field
                    label="Clinic Address"
                    value={R.pick(['address', 'address_geo'])(clinic)}
                    render={({ address, address_geo: { lat, lng } }) => (
                      <>
                        <RowLink href={composeGoogleMapUrl(address)} target="_blank" rel="noopener">
                          {address}
                        </RowLink>
                        <Map
                          markers={[
                            {
                              lat,
                              lng,
                            },
                          ]}
                          containerElement={<RowMap />}
                          onMarkerClick={() => {
                            handleMarkerClick(composeGoogleMapUrl(address));
                          }}
                        />
                      </>
                    )}
                  />
                  <Field
                    label="Opening Hours"
                    value={clinic.opening_time}
                    render={R.pipe(
                      R.map(info => {
                        const weekday = WEEKDAYS[info.day];
                        const [beginHour] = R.split(':', info.start_time);
                        const [endHour] = R.split(':', info.end_time);

                        return `${weekday} ${get12HourFrom24Hour(beginHour)}
                         ${getAMPM(beginHour)} ~ ${get12HourFrom24Hour(endHour)}
                         ${getAMPM(endHour)}`;
                      }),
                      R.intersperse(<br />)
                    )}
                  />
                  <Field
                    label="Emergency Service"
                    value={showAvailableOrNot(clinic.emergency_service_supported)}
                  />
                  <Field
                    label="Emergency Service Contact"
                    value={showAvailableOrNot(clinic.emergency_service_concat)}
                  />
                  <Field
                    label="Out-going Services"
                    value={showAvailableOrNot(clinic.out_going_service_supported)}
                  />
                  <Field
                    label="Vaccine Service"
                    value={showAvailableOrNot(clinic.vaccine_service_supported)}
                  />
                  <Field
                    label="Supported Government Programs"
                    value={clinic.support_government_program}
                    render={generateRowValue(['government_program', 'name'])}
                  />
                  <Field
                    label="Insurance Scheme Supported"
                    value={showAvailableOrNot(clinic.insurance_scheme_supported)}
                  />
                  <Field
                    label="Medical Cards Supported"
                    value={showAvailableOrNot(clinic.medical_cards_supported)}
                  />
                  <Field label="Payment Cards" value={showAvailableOrNot(clinic.payment_cards_supported)} />
                </InfoBox>
              ))}

              {!R.isEmpty(hospitals) && (
                <InfoBox mb={7}>
                  <RowTitle>Other Locations</RowTitle>
                  <Box py={4}>
                    <InfoLabel>Affiliated Hospital</InfoLabel>
                  </Box>
                  <Box mx={-4}>
                    <Flex flexWrap="wrap" flexDirection={['column', 'row']}>
                      {hospitals.map(hospital => (
                        <HyperLink key={hospital.id} href={hospital.website} target="_blank">
                          <LinkCard m={4} width={['auto', 320]}>
                            <Box mb={4}>
                              <Title>{hospital.name}</Title>
                            </Box>
                            <LinkButton variant="link" prefixIcon="LINK" />
                          </LinkCard>
                        </HyperLink>
                      ))}
                    </Flex>
                  </Box>
                </InfoBox>
              )}
            </Box>
          </Section>
        );
      }}
    </Query>
  );
};

export default MemberContent;
